import React, { useState, useEffect, useCallback } from 'react';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const numBadge = (v, danger, warn) => {
  const n = parseInt(v) || 0;
  return (
    <span className={clsx(
      'inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold',
      n === 0 ? 'bg-slate-100 text-slate-400' :
      n >= (danger || 99) ? 'bg-red-100 text-red-700' :
      n >= (warn || 1) ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
    )}>
      {n}
    </span>
  );
};

const COLS = [
  { key: 'staff_name',          label: 'Staff Name' },
  { key: 'role',                label: 'Role',               render: v => v ? v.replace(/_/g, ' ') : '–' },
  { key: 'pay_group',           label: 'Pay Group' },
  { key: 'total_qualifications',label: 'Total Quals',        render: v => numBadge(v, 99, 1) },
  { key: 'valid_count',         label: 'Valid',              render: v => <span className="text-green-700 font-semibold">{v || 0}</span> },
  { key: 'expiring_soon_count', label: 'Expiring (30d)',     render: v => numBadge(v, 99, 1) },
  { key: 'expired_count',       label: 'Expired',            render: v => numBadge(v, 1, 1) },
  { key: 'no_expiry_count',     label: 'No Expiry',          render: v => <span className="text-slate-500">{v || 0}</span> },
  { key: 'total_documents',     label: 'Total Docs',         render: v => numBadge(v, 99, 1) },
  { key: 'docs_expiring_count', label: 'Docs Expiring',      render: v => numBadge(v, 99, 1) },
  { key: 'docs_expired_count',  label: 'Docs Expired',       render: v => numBadge(v, 1, 1) },
  { key: 'earliest_expiry',     label: 'Next Expiry' },
];

export default function EmployeeComplianceExtended() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const pagination = usePagination(data);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/compliance-extended')
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = {
    total: data.length,
    withExpired:  data.filter(r => parseInt(r.expired_count) > 0).length,
    withExpiring: data.filter(r => parseInt(r.expiring_soon_count) > 0).length,
    allGood:      data.filter(r => parseInt(r.expired_count) === 0 && parseInt(r.expiring_soon_count) === 0).length,
  };

  return (
    <ReportLayout title="Employee Compliance Check Extended" data={data} filename="employee_compliance_extended" columns={COLS}
      activeChips={[]} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="text-sm text-slate-500">No filters available — shows all active care workers.</div>
      }>
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Total Staff',       value: summary.total,       color: 'text-slate-900' },
          { label: 'All Compliant',     value: summary.allGood,     color: 'text-green-600' },
          { label: 'Expiring Soon',     value: summary.withExpiring, color: 'text-amber-600' },
          { label: 'Has Expired',       value: summary.withExpired,  color: 'text-red-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
